import axios, { isAxiosError } from 'axios';
import { signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import { firebaseAuth } from '@config/firebase';
import { store } from '@store/index';
import { authActions, selectIsAuthenticated } from '@store/slices/auth';
import { authLog } from '@utils/authLog';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Always attach a FRESH Firebase ID token. `getIdToken()` returns the cached
// token if still valid, otherwise refreshes via the refresh token.
apiClient.interceptors.request.use(async (config) => {
  const user = firebaseAuth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      await handleUnauthorized(error);
    }
    return Promise.reject(error);
  },
);

/**
 * On 401: force a clean sign-out so the next render bounces to /login.
 *
 * Why we don't just refresh + retry: by the time the request fired, the
 * request interceptor already gave us a fresh ID token (Firebase auto-refreshes
 * on `getIdToken()`). A 401 from the server therefore means the server
 * rejected the user (token revoked, claim missing, account not found,
 * deactivated) — refreshing won't help.
 */
async function handleUnauthorized(error: unknown): Promise<void> {
  const message =
    extractServerMessage(error) ?? 'Your session has ended. Please sign in again.';

  // Idempotent — if Redux is already cleared (parallel 401s), skip the toast
  // so we don't spam the user with a duplicate.
  const wasAuthed = selectIsAuthenticated(store.getState());

  authLog('api-401', {
    url: isAxiosError(error) ? error.config?.url : undefined,
    message,
    hadAuthHeader:
      isAxiosError(error) && Boolean(error.config?.headers?.Authorization),
    currentFirebaseUid: firebaseAuth.currentUser?.uid ?? null,
    wasAuthed,
  });

  try {
    await signOut(firebaseAuth);
  } catch {
    /* ignore — we're already tearing down the session */
  }
  store.dispatch(authActions.clearSession());

  if (wasAuthed) toast.error(message);
}

function extractServerMessage(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;
  const data = error.response?.data as
    | { message?: string | string[]; error?: string }
    | undefined;
  if (!data) return undefined;
  if (Array.isArray(data.message)) return data.message.join(', ');
  if (typeof data.message === 'string' && data.message.length > 0) {
    return data.message;
  }
  return data.error;
}
